import { stepSlugs } from './stepSlugs';

/** Direction of a step navigation. */
export type StepDirection = 'next' | 'back';

/**
 * What the event knows about the flow beyond the step. Sent only once known:
 * - `goal`: demo creation flow v3, the goal picked for the AI Agent (`service`, `beratung`, `leads`, `sonstiges`)
 */
export interface StepDetails {
  goal?: string | null;
}

/**
 * The form steps we track, in order.
 * The array index is the `step_index` we pass to GTM.
 */
export const botCreationSteps: string[] = [
  // User defined the site they want a chatbot for
  'define_site',
  // User selected which persona the demo is for
  'select_persona',
  // The system is generating topics. Auto-forwards to next step.
  'thinking',
  // User reviewed generated topics.
  'review_topics',
  // User could optionally customize persona.
  'customize_persona',
  // The system is generating a final overview of the bot. Auto-forwards to next step.
  'generating_overview',
  // User reviewed the summary/overview of the generated bot.
  'review_summary',
  // User filled contact details that passed client-side validation and we're about to submit.
  // This signifies a successful completion of demo creation flow. User is redirected to "thank-you" page.
  'submit',
];

/**
 * Maps our internal step slugs to the tracked step names.
 * Slugs that are missing (e.g. the v3 loading screen) are not tracked.
 */
const stepNameBySlug: Record<string, string> = {
  // Demo creation flow v3 (`src/components/demoV3`): `botCreationSteps` stays as is, so the
  // indices are 0, 1, 3 and 7. The loading screen is deliberately not a funnel step.
  domain: 'define_site',
  goal: 'select_persona',
  preview: 'review_topics',
  signup: 'submit',

  // Legacy flow
  [stepSlugs.stepCompany]: 'define_site',
  [stepSlugs.stepPersona]: 'select_persona',
  [stepSlugs.stepQuestionsLoading]: 'thinking',
  [stepSlugs.stepQuestions]: 'review_topics',
  [stepSlugs.stepEditPersona]: 'customize_persona',
  [stepSlugs.stepIntentsLoading]: 'generating_overview',
  [stepSlugs.stepIntents]: 'review_summary',
  [stepSlugs.stepContact]: 'submit',
};

let stepEnteredAt = performance.now();

/**
 * Restart the time-on-step measurement, without pushing an event.
 * Call when the user actually starts the flow (e.g. demo mounted),
 * since this module may be imported long before that.
 */
export function resetStepTimer(): void {
  stepEnteredAt = performance.now();
}

/**
 * Push a step-navigation event to GTM.
 * Call every time we *leave* a step, after client-side validation passed.
 *
 * On the final step ('submit'), call this *before* contact details get
 * submitted to our API and awaited. The browser beacon should handle page
 * unload to flush events, and the API roundtrip should provide enough delay
 * for the async GA4 chain to finish before the redirect.
 *
 * @param name The tracked step name (see {@link botCreationSteps})
 * @param direction The step delta direction
 * @param details See {@link StepDetails}
 */
export function trackStep(name: string, direction: StepDirection, details: StepDetails = {}): void {
  pushStep(name, direction, details);
}

/**
 * Same as {@link trackStep}, but resolves once GTM has sent the event, for when
 * the page unloads right after (e.g. a redirect). Resolves after `timeoutMs`
 * at the latest, also when GTM never loaded (e.g. blocked), so it can't hold
 * the visitor up.
 *
 * @param name The tracked step name (see {@link botCreationSteps})
 * @param direction The step delta direction
 * @param details See {@link StepDetails}
 * @param timeoutMs How long to wait for GTM at most
 */
export function trackStepAndWait(name: string, direction: StepDirection, details: StepDetails = {}, timeoutMs = 1500): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    // GTM enforces `eventTimeout` itself, which doesn't help when it isn't there at all
    window.setTimeout(finish, timeoutMs);
    pushStep(name, direction, details, { eventCallback: finish, eventTimeout: timeoutMs });
  });
}

function pushStep(name: string, direction: StepDirection, details: StepDetails = {}, extra: Record<string, unknown> = {}): void {
  const now = performance.now();

  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({
    event: 'create_demo_chatbot',
    step_index: botCreationSteps.indexOf(name),
    step_name: name,
    direction,
    time_on_step_ms: Math.round(now - stepEnteredAt),
    ...(details.goal ? { goal: details.goal } : {}),
    ...extra,
  });

  stepEnteredAt = now;
}

/**
 * Same as {@link trackStep}, but takes an internal step slug.
 * Unknown slugs are ignored.
 *
 * @param slug One of the {@link stepSlugs}
 * @param direction The step delta direction
 * @param details See {@link StepDetails}
 */
export function trackStepBySlug(slug: string, direction: StepDirection, details: StepDetails = {}): void {
  const name = stepNameBySlug[slug];
  if (!name) return;

  trackStep(name, direction, details);
}

/**
 * The status page revealed its video (demo creation flow v3), which then plays by itself.
 * Not a funnel step, so not a `create_demo_chatbot` event.
 */
export function trackVideoPlay(): void {
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event: 'video_play' });
}

const TAG_MANAGER_SRC = 'https://data.moin.ai/tm.js';

/**
 * Loads our tag manager, once. The funnel pages load it themselves, the rest
 * of the Hub doesn't.
 */
export function loadTagManager(): void {
  (window as any).dataLayer = (window as any).dataLayer || [];

  if (document.querySelector(`script[src="${TAG_MANAGER_SRC}"]`)) {
    return;
  }

  (window as any).dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const gtmScript = document.createElement('script');
  gtmScript.async = true;
  gtmScript.src = TAG_MANAGER_SRC;

  const nonceElement: any = document.querySelector('[nonce]');
  const nonce = nonceElement?.nonce || nonceElement?.getAttribute('nonce');
  if (nonce) {
    gtmScript.setAttribute('nonce', nonce);
  }

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(gtmScript, firstScript);
  } else {
    document.head.appendChild(gtmScript);
  }
}
